/**
 * Floor Detection Area Properties
 *
 * Stores the user-drawn selection area from AutoFloorTool as a project property.
 * Uses 4 corner points (COORDINATE) to support off-axis / rotated rectangles.
 * This area is used by FloorToolBase to size floor visualization volumes and
 * preview outlines to the selected region rather than the full scene bounds.
 */

import { PropertyType, PropertyValueType } from '../../types';
import { propertyRegistry } from '../../PropertyRegistry';
import { ReactiveProperty } from '../../reactive/ReactiveProperty';
import { getGlobalPropertyService } from '../../reactive/globalPropertyService';

// ============================================================================
// TYPES
// ============================================================================

type Coordinate = { x: number; y: number; z: number };

export type FloorDetectionAreaCorners = [Coordinate, Coordinate, Coordinate, Coordinate];

/** Fixed object identifier for project-level floor detection area */
const FLOOR_DETECTION_AREA_OBJECT_ID = 'floor_detection_area';

// ============================================================================
// PROPERTY REGISTRATION
// ============================================================================

/**
 * Register floor detection area properties in the registry.
 * Call this once at app initialization.
 */
export function registerFloorDetectionAreaProperties() {
  for (let i = 0; i < 4; i++) {
    const id = `floor_detection_area_corner_${i}`;
    if (!propertyRegistry.isRegistered(id)) {
      propertyRegistry.register({
        type: PropertyType.PROJECT_GLOBAL,
        identifier: id,
        name: `Floor Detection Area Corner ${i}`,
        valueType: PropertyValueType.COORDINATE,
        defaultValue: { x: 0, y: 0, z: 0 },
        annotationTypes: [],
        description: `Corner ${i} of the floor detection area polygon`,
      });
    }
  }
}

// Auto-register on import
registerFloorDetectionAreaProperties();

// ============================================================================
// NON-HOOK HELPERS FOR POTREE (uses global property service)
// ============================================================================

function getCornerProperty(index: number): ReactiveProperty<Coordinate> {
  const service = getGlobalPropertyService();
  return service.getProperty(`floor_detection_area_corner_${index}`, FLOOR_DETECTION_AREA_OBJECT_ID, PropertyType.PROJECT_GLOBAL) as ReactiveProperty<Coordinate>;
}

/**
 * Save floor detection area corners as a batch update
 */
export async function saveFloorDetectionArea(corners: FloorDetectionAreaCorners): Promise<void> {
  const service = getGlobalPropertyService();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const updates: Array<[ReactiveProperty<any>, any]> = corners.map((corner, i) => [getCornerProperty(i), corner]);
  await service.batchUpdateProperties(updates);
}

/**
 * Load floor detection area corners.
 * Returns null if no area has been saved (all corners are default zero values).
 */
export function loadFloorDetectionArea(): FloorDetectionAreaCorners | null {
  const corners: Coordinate[] = [];
  let allZero = true;

  for (let i = 0; i < 4; i++) {
    const prop = getCornerProperty(i);
    const val = prop.value;
    corners.push(val);
    if (val.x !== 0 || val.y !== 0 || val.z !== 0) {
      allZero = false;
    }
  }

  if (allZero) return null;

  return corners as FloorDetectionAreaCorners;
}
